import { useCallback } from 'react'
import { useAdpSessionStore } from './useAdpSessionStore'
import { useAppDispatch } from '@/app/reduxHooks'
import { markValidationDirty } from '@/features/adp/state/validationSlice'

export interface IntegrationPayload {
  [key: string]: unknown
}

interface IntegrationEntry {
  data: IntegrationPayload
  createdAt: number
}

interface NodeIntegrationWrapper {
  integrationData?: IntegrationEntry
}

/**
 * Session storage for integration nodes (Join `JR`, Union `UN`, …). Mirrors
 * `useTransformationData` but writes to a separate `integrationData` key so
 * readers can tell the two domains apart without inspecting the abbreviation.
 *
 * Why a dedicated key: integration nodes share the data *shape*
 * (`{id, name, abbreviation, data: [items]}`) with transformations, but their
 * canvas semantics differ — integrations merge multiple inputs, transformations
 * map one. Keeping the storage parallel keeps the type domains explicit and
 * leaves room for integration-only fields (e.g. canvas wire metadata) without
 * polluting the transformation type.
 */
export function useIntegrationData() {
  const { getNode, setNode, getAll } = useAdpSessionStore()
  const dispatch = useAppDispatch()

  const setIntegrationData = useCallback(
    (nodeId: string, payload: IntegrationPayload) => {
      const entry: IntegrationEntry = {
        data: payload,
        createdAt: Date.now(),
      }

      setNode(nodeId, {
        integrationData: entry,
      })
      dispatch(markValidationDirty())
    },
    [dispatch, setNode]
  )

  const updateIntegrationData = useCallback(
    (nodeId: string, payload: IntegrationPayload) => {
      const existing = getNode(nodeId) as NodeIntegrationWrapper | null

      const nextData: IntegrationPayload = existing?.integrationData
        ? { ...existing.integrationData.data, ...payload }
        : { ...payload }

      setNode(nodeId, {
        integrationData: {
          data: nextData,
          createdAt: existing?.integrationData?.createdAt ?? Date.now(),
        },
      })
      dispatch(markValidationDirty())
    },
    [dispatch, getNode, setNode]
  )

  const getAllIntegrationData = useCallback((): IntegrationEntry[] => {
    const all = getAll()

    return Object.values(all)
      .map(node => (node as NodeIntegrationWrapper).integrationData)
      .filter((v): v is IntegrationEntry => Boolean(v))
  }, [getAll])

  const getIntegrationDataByNodeId = useCallback(
    (nodeId: string): IntegrationEntry | null => {
      const node = getNode(nodeId) as NodeIntegrationWrapper | null
      return node?.integrationData ?? null
    },
    [getNode]
  )

  const clearIntegrationData = useCallback(
    (nodeId: string) => {
      const existing = getNode(nodeId) as NodeIntegrationWrapper | null
      if (!existing?.integrationData) return

      setNode(nodeId, {
        integrationData: undefined,
      })
      dispatch(markValidationDirty())
    },
    [dispatch, getNode, setNode]
  )

  return {
    setIntegrationData,
    updateIntegrationData,
    getAllIntegrationData,
    getIntegrationDataByNodeId,
    clearIntegrationData,
  }
}
